import { toast } from "react-toastify";
import { Style } from "cpl-react-common/Helpers/AlertHelpers";
import { setMessage, setShowMessage, setStyle } from "./SignalRAlertSlice";
import { setCPLMaintenanceDates } from "app/system/maintenance/MaintenanceSlice";
import { setPercentage } from "./SignalRProgressBarSlice";
import { IMaintenanceService } from "services/MaintenanceService";
import { container } from "setup/dependency/inversify.config";
import { SERVICE_TYPES } from "setup/dependency/ServiceTypes";
import { handleResponseModel } from "app/modules/backgroundActivities/components/BackgroundActivitySlice";
import { getUserTasks } from "app/modules/scheduling/userTasks/components/UserTasksTableSlice";
import {
  setPrintProgressResponse,
  setResendEmailProgressResponse,
} from "app/modules/serviceChargeInvoicing/components/ServiceChargeInvoicingSummariesSlice";
import { setGeneratingDevelopmentChargesPreList } from "app/modules/serviceChargeInvoicing/components/steps/DevelopmentChargesPreList/DevelopmentChargesPreListSlice";
import {
  setGeneratingInvoices,
  setProgress,
} from "app/modules/serviceChargeInvoicing/components/steps/InvoicePreview/InvoicesPreviewSlice";
import {
  setIsSavingInvoices,
  setEmailProgressResponse,
} from "app/modules/serviceChargeInvoicing/components/steps/InvoiceSending/InvoiceSendingSlice";
import { UserTaskFiltersModel } from "services/api/cplapi/CPLAPI";
import { InvoiceDistributionResponseModel } from "services/classes/InvoiceDistributionResponseModel";
import { getServiceChargeInvoiceProcessSelectionModel } from "app/modules/serviceChargeInvoicing/components/ServiceChargeInvoicingSlice";
import { addRunStep } from "app/modules/aiAssistant/AIAssistantSlice";

/* eslint-disable @typescript-eslint/no-explicit-any */

export const displayToastMessage = (message: string, style: Style) => {
  switch (style) {
    case Style.Danger:
      toast.error(message);
      break;
    case Style.Info:
      toast.info(message);
      break;
    case Style.Success:
      toast.success(message);
      break;
    case Style.Warning:
      toast.warning(message);
      break;
  }
};

export const displayAlert = (dispatch: (arg0: any) => any, message: string, style: Style) => {
  dispatch(setMessage(message));
  dispatch(setStyle(style));
  dispatch(setShowMessage(true));
};

export const setupCommonSignalRHandlers = (connection: signalR.HubConnection, dispatch: (arg0: any) => any) => {
  connection.on("SendCPLMaintenanceDates", (dates) => {
    const maintenanceService = container.get<IMaintenanceService>(SERVICE_TYPES.CPLAuthMaintenanceClient);
    const convertedDates = maintenanceService.convertMaintenanceDates(dates);
    dispatch(setCPLMaintenanceDates(convertedDates));
  });
  connection.on("UpdateProgress", (percentage) => dispatch(setPercentage(percentage)));

  connection.on("SendSuccessAlert", (notification) => displayAlert(dispatch, notification, Style.Success));
  connection.on("SendWarningAlert", (notification) => displayAlert(dispatch, notification, Style.Warning));
  connection.on("SendInfoAlert", (notification) => displayAlert(dispatch, notification, Style.Info));
  connection.on("SendDangerAlert", (notification) => displayAlert(dispatch, notification, Style.Danger));
  connection.on("SendSuccessToast", (notification) => displayToastMessage(notification, Style.Success));
  connection.on("SendWarningToast", (notification) => displayToastMessage(notification, Style.Warning));
  connection.on("SendInfoToast", (notification) => displayToastMessage(notification, Style.Info));
  connection.on("SendDangerToast", (notification) => displayToastMessage(notification, Style.Danger));
};

export const setupSignalRServiceHandlers = (connection: signalR.HubConnection, dispatch: (arg0: any) => any) => {
  const defaultUserTaskFilters = new UserTaskFiltersModel({
    includeAllCompany: false,
    includeSubordinates: false,
    includeUserGroup: false,
    includeArchived: false,
  });

  connection.on("JobCreated", (notification) => {
    dispatch(getUserTasks({ userTaskFilters: defaultUserTaskFilters }));
    displayToastMessage(notification, Style.Success);
  });
  connection.on("EstimateRequestCreated", (notification) => {
    dispatch(getUserTasks({ userTaskFilters: defaultUserTaskFilters }));
    displayToastMessage(notification, Style.Success);
  });
  connection.on("ProposedWorkCreated", (notification) => {
    dispatch(getUserTasks({ userTaskFilters: defaultUserTaskFilters }));
    displayToastMessage(notification, Style.Success);
  });

  connection.on("UpdateBackgroundProcessPercentage", (notification) => {
    dispatch(handleResponseModel(notification));
  });

  connection.on("AssistimoRunStep", (runID: string, step: string) => {
    dispatch(addRunStep({ runID, step }));
  });

  connection.on("ServiceChargeInvoicingPreListLoaded", () => {
    dispatch(setGeneratingDevelopmentChargesPreList(false));
    dispatch(getServiceChargeInvoiceProcessSelectionModel());
  });

  connection.on("ServiceChargeInvoicesLoaded", () => {
    dispatch(setGeneratingInvoices(false));
    dispatch(setProgress(100));
  });

  connection.on("ServiceChargeInvoicesSaved", () => {
    dispatch(setIsSavingInvoices(false));
  });

  connection.on(
    "ServiceChargeInvoiceEmailsSent",
    (invoiceDistributionResponseModel: InvoiceDistributionResponseModel) => {
      dispatch(setEmailProgressResponse(invoiceDistributionResponseModel));
    }
  );

  connection.on(
    "ServiceChargeInvoicePrintsSent",
    (invoiceDistributionResponseModel: InvoiceDistributionResponseModel) => {
      dispatch(setPrintProgressResponse(invoiceDistributionResponseModel));
    }
  );

  connection.on(
    "ServiceChargeInvoiceEmailsResent",
    (invoiceDistributionResponseModel: InvoiceDistributionResponseModel) => {
      dispatch(setResendEmailProgressResponse(invoiceDistributionResponseModel));
    }
  );
};
